import api from '../api/apiClient';
import apiProofreadCaption from '../api/apiProofreadCaption';
import apiReadCaptionImage from '../api/apiReadCaptionImage';
import { websocketService } from '../services/websocketService';
import { trackCameraStarted } from './tracking';
import { selectPaddleCaptionRegion } from './captionRegion';

let sharedOcrEngine = null;
let sharedOcrEnginePromise = null;

// Desktop OCR preparation progress (model download + engine startup), so the
// UI can show a real progress bar on a cold cache instead of a spinner.
// Total used until the responses report their content-length.
const OCR_DOWNLOAD_FALLBACK_TOTAL_BYTES = 32 * 1024 * 1024;
let ocrPreparation = { phase: 'idle', loaded: 0, total: 0 };
const ocrPreparationListeners = new Set();

const notifyOcrPreparation = update => {
    ocrPreparation = { ...ocrPreparation, ...update };
    ocrPreparationListeners.forEach(listener => listener(ocrPreparation));
};

export const subscribeCaptionOcrPreparation = listener => {
    ocrPreparationListeners.add(listener);
    listener(ocrPreparation);
    return () => ocrPreparationListeners.delete(listener);
};

const fetchModelAssetBlob = async (url, onChunkBytes, onTotalBytes) => {
    const response = await fetch(url);
    if (!response.ok) {
        throw new Error(`Caption model download failed (${response.status})`);
    }

    const contentLength = Number(response.headers.get('content-length'));
    if (Number.isFinite(contentLength) && contentLength > 0) {
        onTotalBytes(contentLength);
    }

    if (!response.body?.getReader) {
        const blob = await response.blob();
        onChunkBytes(blob.size);
        return blob;
    }

    const reader = response.body.getReader();
    const chunks = [];
    for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        chunks.push(value);
        onChunkBytes(value.byteLength);
    }
    return new Blob(chunks);
};

export const isTouchDevice = () => {
    if (typeof navigator === 'undefined') return false;

    const userAgent = navigator.userAgent || '';
    return (
        // Some desktop pen/touch drivers expose one phantom touch point.
        navigator.maxTouchPoints > 1 ||
        /Android|iPad|iPhone|iPod/i.test(userAgent)
    );
};

const ocrAssetUrl = (fileName) =>
    `${process.env.PUBLIC_URL || ''}/static/ocr-models/${fileName}`;

const DESKTOP_OCR_MODELS = {
    textDetectionModelName: 'PP-OCRv6_small_det',
    textDetectionModelFile:
        'PP-OCRv6_small_det_onnx_infer.73f4b3915bd1.tar',
    textRecognitionModelName: 'PP-OCRv6_small_rec',
    textRecognitionModelFile:
        'PP-OCRv6_small_rec_onnx_infer.1313e1140ea4.tar',
};

// Touch devices cannot hold the PaddleOCR WASM runtime in memory without
// crashing the tab, so they upload gated caption-strip frames and let the
// backend's multimodal model read them instead.
export const getCaptionOcrStrategy = useTouchCompatibilityMode =>
    useTouchCompatibilityMode ? 'vision' : 'paddle';

export const getCaptionOcrModelConfig = () => {
    const models = DESKTOP_OCR_MODELS;
    return {
        textDetectionModelName: models.textDetectionModelName,
        textDetectionModelAsset: {
            url: ocrAssetUrl(models.textDetectionModelFile),
        },
        textRecognitionModelName: models.textRecognitionModelName,
        textRecognitionModelAsset: {
            url: ocrAssetUrl(models.textRecognitionModelFile),
        },
    };
};

const createSharedOcrEngine = () => {
    if (sharedOcrEngine) return Promise.resolve(sharedOcrEngine);
    if (sharedOcrEnginePromise) return sharedOcrEnginePromise;

    const modelConfig = getCaptionOcrModelConfig();

    sharedOcrEnginePromise = (async () => {
        // Download the model archives here (instead of inside PaddleOCR) so
        // real byte progress can be reported, then hand the engine blob URLs.
        let loadedBytes = 0;
        let knownTotalBytes = 0;
        const currentTotal = () =>
            knownTotalBytes || OCR_DOWNLOAD_FALLBACK_TOTAL_BYTES;
        const onChunkBytes = bytes => {
            loadedBytes += bytes;
            notifyOcrPreparation({
                phase: 'downloading',
                loaded: loadedBytes,
                total: currentTotal(),
            });
        };
        const onTotalBytes = bytes => {
            knownTotalBytes += bytes;
            notifyOcrPreparation({ total: currentTotal() });
        };

        notifyOcrPreparation({
            phase: 'downloading',
            loaded: 0,
            total: currentTotal(),
        });

        const [{ PaddleOCR }, detectionBlob, recognitionBlob] =
            await Promise.all([
                import('@paddleocr/paddleocr-js'),
                fetchModelAssetBlob(
                    modelConfig.textDetectionModelAsset.url,
                    onChunkBytes,
                    onTotalBytes
                ),
                fetchModelAssetBlob(
                    modelConfig.textRecognitionModelAsset.url,
                    onChunkBytes,
                    onTotalBytes
                ),
            ]);

        notifyOcrPreparation({ phase: 'starting' });
        const detectionUrl = URL.createObjectURL(detectionBlob);
        const recognitionUrl = URL.createObjectURL(recognitionBlob);

        try {
            const engine = await PaddleOCR.create({
                textDetectionModelName: modelConfig.textDetectionModelName,
                textDetectionModelAsset: { url: detectionUrl },
                textRecognitionModelName:
                    modelConfig.textRecognitionModelName,
                textRecognitionModelAsset: { url: recognitionUrl },
                worker: true,
                ortOptions: {
                    // Serve the exact ONNX Runtime assets installed with PaddleOCR.
                    // A hardcoded CDN version can silently drift from the bundled
                    // JavaScript runtime and also makes OCR depend on a third party.
                    wasmPaths: `${process.env.PUBLIC_URL || ''}/static/ort/`,
                    numThreads: 1,
                    simd: true,
                },
                textDetectionBatchSize: 1,
                textRecognitionBatchSize: 4,
            });
            sharedOcrEngine = engine;
            notifyOcrPreparation({ phase: 'ready' });
            return engine;
        } finally {
            URL.revokeObjectURL(detectionUrl);
            URL.revokeObjectURL(recognitionUrl);
        }
    })().catch(error => {
        sharedOcrEnginePromise = null;
        notifyOcrPreparation({ phase: 'error' });
        throw error;
    });

    return sharedOcrEnginePromise;
};

export const preloadCaptionOcrEngine = () => {
    // Touch devices read captions server-side; there is no engine to preload.
    if (isTouchDevice()) return Promise.resolve(null);

    return createSharedOcrEngine().catch(error => {
        console.warn('Local caption OCR could not be preloaded:', error);
        return null;
    });
};

const createUsageSessionId = () => {
    if (window.crypto?.randomUUID) {
        return window.crypto.randomUUID();
    }
    return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
};

export class CaptionController {
    constructor(
        addMessage,
        onClose = null,
        onReady = null,
        onLimitExceeded = null,
        onCameraAvailability = null,
        onOcrStatus = null,
        onZoomChange = null
    ) {
        this.addMessage = addMessage;
        this.onClose = onClose;
        this.onReady = onReady;
        this.onLimitExceeded = onLimitExceeded;
        this.onCameraAvailability = onCameraAvailability;
        this.onOcrStatus = onOcrStatus;
        this.onZoomChange = onZoomChange;

        // Camera capture state
        this.videoElement = null;
        this.canvasElement = null;
        this.refinementCanvasElement = null;
        this.cameraStream = null;
        this.facingMode = 'environment';
        this.hasMultipleCameras = false;
        this.isSwitchingCamera = false;
        this.captionInterval = null;
        this.shownCaptions = new Set();
        this.shownCaptionTimes = new Map();
        this.SHOWN_CAPTION_TTL_MS = 45000;
        this.CAPTION_INTERVAL_MS = 1000;
        this.OCR_MAX_DIMENSION = 1600;
        this.ocrStrategy = getCaptionOcrStrategy(isTouchDevice());
        this.LOCAL_OCR_MIN_CONFIDENCE = 55;
        this.LOCAL_OCR_IMMEDIATE_CONFIDENCE = 92;
        this.LOCAL_OCR_STABLE_OBSERVATIONS = 2;
        this.OCR_GUIDANCE_FAILURES = 3;
        this.OCR_UNSUPPORTED_FAILURES = 8;
        this.OCR_NOTIFICATION_COOLDOWN_MS = 60000;
        this.captureInFlight = false;
        this.ocrEngine = null;
        this.ocrEnginePromise = null;
        this.ocrEngineGeneration = 0;
        this.ocrRetryAfter = 0;
        this.pendingOcrCaption = null;
        this.captionProofreadQueue = Promise.resolve();
        this.captionProofreadGeneration = 0;
        this.sourceLanguage = null;
        this.consecutiveOcrFailures = 0;
        this.consecutiveUnreadableOcrFailures = 0;
        this.consecutiveOcrRuntimeErrors = 0;
        this.ocrNotificationTimes = new Map();

        // Server-side caption reading (touch devices). A tiny binarized
        // thumbnail of the caption strip gates uploads so an unchanged
        // subtitle is never re-sent, and a minimum interval bounds the
        // worst-case AI spend regardless of how busy the video is.
        this.VISION_MIN_UPLOAD_INTERVAL_MS = 2000;
        this.VISION_UPLOAD_MAX_WIDTH = 768;
        this.VISION_JPEG_QUALITY = 0.7;
        this.VISION_GATE_WIDTH = 64;
        this.VISION_GATE_HEIGHT = 20;
        this.VISION_GATE_STRIP_RATIO = 0.55;
        this.VISION_GATE_BRIGHTNESS_THRESHOLD = 178;
        this.VISION_GATE_MIN_BRIGHT_RATIO = 0.005;
        this.VISION_GATE_CHANGED_DIFF_RATIO = 0.04;
        this.VISION_GATE_STABLE_DIFF_RATIO = 0.1;
        this.VISION_GATE_MAX_UNSTABLE_FRAMES = 3;
        this.VISION_NEGATIVE_RETRY_MS = 10000;
        this.visionGateCanvasElement = null;
        this.visionUploadCanvasElement = null;
        this.previousVisionSignature = null;
        this.lastUploadedVisionSignature = null;
        this.lastUploadedVisionAt = 0;
        this.lastUploadedVisionWasCaption = false;
        this.lastVisionUploadAt = 0;
        this.visionUnstableFrames = 0;
        this.visionUploadInFlight = false;
        this.visionUploadTask = null;
        this.visionGeneration = 0;
        this.visionReadZoneContainer = null;
        this.visionReadZoneElement = null;
        this.visionReadZoneHintElement = null;
        this.boundUpdateVisionReadZone = null;

        // Viewing-time billing is independent of how often local OCR needs AI.
        this.USAGE_HEARTBEAT_MS = 10000;
        this.usageHeartbeatInterval = null;
        this.usageHeartbeatInFlight = false;
        this.usageHeartbeatGeneration = 0;
        this.lastUsageHeartbeatAt = null;
        this.usageSessionId = null;
        this.usageHeartbeatSequence = 0;
        this.pendingUsageHeartbeat = null;
        this.consecutiveUsageHeartbeatFailures = 0;
        this.MAX_USAGE_HEARTBEAT_FAILURES = 3;

        // Zoom
        this.zoomLevel = 1;
        this.MIN_ZOOM = 1;
        this.MAX_ZOOM = 4;
        this.ZOOM_STEP = 0.5;
        this.supportsNativeZoom = false;
        this.nativeZoomRange = null;
        this.AUTO_ZOOM_MIN_LINE_RATIO = 0.055;
        this.AUTO_ZOOM_MAX_LINE_RATIO = 0.12;
        this.AUTO_ZOOM_STABLE_OBSERVATIONS = 3;
        this.AUTO_ZOOM_COOLDOWN_MS = 4000;
        this.AUTO_ZOOM_MANUAL_PAUSE_MS = 15000;
        this.AUTO_ZOOM_RECOVERY_MISSES = 8;
        this.autoZoomSamples = [];
        this.autoZoomMissingFrames = 0;
        this.autoZoomLastAdjustedAt = 0;
        this.autoZoomPausedUntil = 0;

        // Auto-close timeout
        this.lastCaptionTime = null;
        this.noCaptionTimeoutCheck = null;
        this.NO_CAPTION_TIMEOUT_MS = 60000;

        this.isListening = false;

        // Wake lock to keep screen on
        this.wakeLock = null;
    }

    requestCameraStream(facingMode = this.facingMode, exact = false) {
        return navigator.mediaDevices.getUserMedia({
            video: {
                facingMode: exact
                    ? { exact: facingMode }
                    : { ideal: facingMode },
                width: { ideal: 1920 },
                height: { ideal: 1080 },
            },
        });
    }

    updateCameraCapabilities() {
        this.supportsNativeZoom = false;
        this.nativeZoomRange = null;
        this.MAX_ZOOM = 4;

        const videoTrack = this.cameraStream?.getVideoTracks()[0];
        if (!videoTrack) return;

        try {
            const capabilities = videoTrack.getCapabilities();
            if (capabilities.zoom) {
                this.supportsNativeZoom = true;
                this.nativeZoomRange = {
                    min: capabilities.zoom.min,
                    max: capabilities.zoom.max,
                };
                const cameraMaxZoom = capabilities.zoom.max / capabilities.zoom.min;
                this.MAX_ZOOM = Math.min(this.MAX_ZOOM, cameraMaxZoom);
            }
        } catch {
            console.log('Could not detect zoom capabilities');
        }
    }

    async detectMultipleCameras() {
        try {
            const devices = await navigator.mediaDevices.enumerateDevices();
            this.hasMultipleCameras =
                devices.filter(device => device.kind === 'videoinput').length > 1;
        } catch {
            this.hasMultipleCameras = false;
        }

        if (this.onCameraAvailability) {
            this.onCameraAvailability(this.hasMultipleCameras);
        }
    }

    async switchCamera() {
        if (
            !this.hasMultipleCameras ||
            this.isSwitchingCamera ||
            !this.videoElement
        ) {
            return false;
        }

        this.isSwitchingCamera = true;
        const previousFacingMode = this.facingMode;
        const nextFacingMode =
            previousFacingMode === 'environment' ? 'user' : 'environment';

        this.cameraStream?.getVideoTracks().forEach(track => track.stop());
        let nextStream = null;

        try {
            nextStream = await this.requestCameraStream(nextFacingMode, true);
            this.cameraStream = nextStream;
            this.videoElement.srcObject = this.cameraStream;
            await this.videoElement.play();
            this.facingMode = nextFacingMode;
            this.zoomLevel = 1;
            this.videoElement.style.transform = 'scale(1)';
            this.resetAutoZoom();
            this.onZoomChange?.(this.zoomLevel);
            this.updateCameraCapabilities();
            return true;
        } catch (error) {
            console.warn('Could not switch camera, restoring previous camera:', error);
            nextStream?.getTracks().forEach(track => track.stop());
            try {
                this.cameraStream = await this.requestCameraStream(previousFacingMode);
                this.videoElement.srcObject = this.cameraStream;
                await this.videoElement.play();
                this.zoomLevel = 1;
                this.videoElement.style.transform = 'scale(1)';
                this.resetAutoZoom();
                this.onZoomChange?.(this.zoomLevel);
                this.updateCameraCapabilities();
            } catch (restoreError) {
                console.error('Could not restore camera:', restoreError);
                this.cleanup();
                if (this.onClose) this.onClose();
                return false;
            }
        } finally {
            this.isSwitchingCamera = false;
        }

        return false;
    }

    cleanup() {
        if (this.captionInterval) {
            clearInterval(this.captionInterval);
            this.captionInterval = null;
        }
        if (this.noCaptionTimeoutCheck) {
            clearInterval(this.noCaptionTimeoutCheck);
            this.noCaptionTimeoutCheck = null;
        }
        this.stopUsageHeartbeat();
        this.removeVisionReadZone();
        if (this.cameraStream) {
            this.cameraStream.getTracks().forEach(track => track.stop());
            this.cameraStream = null;
        }
        if (this.videoElement) {
            this.videoElement.remove();
            this.videoElement = null;
        }
        if (this.canvasElement) {
            this.canvasElement.remove();
            this.canvasElement = null;
        }
        this.refinementCanvasElement = null;
        this.visionGateCanvasElement = null;
        this.visionUploadCanvasElement = null;
        this.resetVisionGate();
        this.terminateOcrEngine();
        if (this.wakeLock) {
            this.wakeLock.release();
            this.wakeLock = null;
        }
        this.isListening = false;
        this.captureInFlight = false;
        this.pendingOcrCaption = null;
        this.captionProofreadGeneration += 1;
        this.captionProofreadQueue = Promise.resolve();
        this.consecutiveOcrFailures = 0;
        this.consecutiveUnreadableOcrFailures = 0;
        this.consecutiveOcrRuntimeErrors = 0;
        this.ocrNotificationTimes.clear();
        this.shownCaptionTimes.clear();
        this.isSwitchingCamera = false;
        this.hasMultipleCameras = false;
        this.resetAutoZoom();
        if (this.zoomLevel !== 1) {
            this.zoomLevel = 1;
            this.onZoomChange?.(this.zoomLevel);
        }
        if (this.onCameraAvailability) this.onCameraAvailability(false);
    }

    resetAutoZoom() {
        this.autoZoomSamples = [];
        this.autoZoomMissingFrames = 0;
        this.autoZoomLastAdjustedAt = 0;
        this.autoZoomPausedUntil = 0;
    }

    async setZoomLevel(requestedZoom, { manual = false } = {}) {
        if (manual) {
            this.autoZoomPausedUntil = Date.now() + this.AUTO_ZOOM_MANUAL_PAUSE_MS;
            this.autoZoomSamples = [];
            this.autoZoomMissingFrames = 0;
        }

        const newZoom = Math.max(
            this.MIN_ZOOM,
            Math.min(this.MAX_ZOOM, requestedZoom)
        );

        if (newZoom === this.zoomLevel) return;
        this.zoomLevel = newZoom;
        this.onZoomChange?.(this.zoomLevel);

        if (this.zoomLabel) {
            this.zoomLabel.textContent = `${this.zoomLevel}x`;
        }

        if (this.supportsNativeZoom && this.cameraStream) {
            try {
                const videoTrack = this.cameraStream.getVideoTracks()[0];
                if (videoTrack) {
                    const { min, max } = this.nativeZoomRange;
                    const nativeZoom = Math.min(max, min * this.zoomLevel);

                    await videoTrack.applyConstraints({
                        advanced: [{ zoom: nativeZoom }],
                    });

                    if (this.videoElement) {
                        this.videoElement.style.transform = 'scale(1)';
                    }
                    return;
                }
            } catch (err) {
                console.warn('Native zoom failed, falling back to digital:', err);
                this.supportsNativeZoom = false;
                this.nativeZoomRange = null;
                this.MAX_ZOOM = 4;
            }
        }

        if (this.videoElement) {
            this.videoElement.style.transform = `scale(${this.zoomLevel})`;
        }
    }

    async adjustZoom(delta) {
        return this.setZoomLevel(this.zoomLevel + delta, { manual: true });
    }

    getAutoZoomMeasurement(region) {
        const imageWidth = this.analysisCanvasElement?.width;
        const imageHeight = this.analysisCanvasElement?.height;
        if (!imageWidth || !imageHeight || !region?.width || !region?.height) {
            return null;
        }

        const lineCount = Math.max(
            1,
            String(region.text || '').split('\n').filter(Boolean).length
        );

        return {
            lineHeightRatio: region.height / imageHeight / lineCount,
            left: region.left / imageWidth,
            right: (region.left + region.width) / imageWidth,
            top: region.top / imageHeight,
            bottom: (region.top + region.height) / imageHeight,
        };
    }

    getCaptionLayout(region) {
        const measurement = this.getAutoZoomMeasurement(region);
        if (!measurement) return null;

        return {
            centerX: (measurement.left + measurement.right) / 2,
            centerY: (measurement.top + measurement.bottom) / 2,
            widthRatio: measurement.right - measurement.left,
            heightRatio: measurement.bottom - measurement.top,
        };
    }

    isLikelyCaptionCandidate(region, cleanedText = this.cleanOcrText(region?.text)) {
        const words = cleanedText.match(/[\p{L}\p{N}]+/gu) || [];
        const compactText = words.join('');
        const hasTerminalPunctuation = /[.!?…][\u201d"'’)]*$/u.test(
            cleanedText.trim()
        );

        if (!compactText) return false;
        if (/^\p{N}+$/u.test(compactText) && compactText.length <= 6) {
            return false;
        }

        if (
            words.length === 1 &&
            /^(?:svt[12]?|tv4)$/iu.test(words[0])
        ) {
            return false;
        }

        const measurement = this.getAutoZoomMeasurement(region);
        const isShortCornerOverlay =
            words.length <= 2 &&
            !hasTerminalPunctuation &&
            measurement &&
            measurement.top < 0.35 &&
            (measurement.right < 0.35 || measurement.left > 0.65);

        return !isShortCornerOverlay;
    }

    canKeepRegionInNextZoom(measurement, nextZoom) {
        if (nextZoom <= this.zoomLevel) return true;

        const retainedRatio = this.zoomLevel / nextZoom;
        const inset = (1 - retainedRatio) / 2;
        const safetyPadding = 0.025;
        return (
            measurement.left >= inset + safetyPadding &&
            measurement.right <= 1 - inset - safetyPadding &&
            measurement.top >= inset + safetyPadding &&
            measurement.bottom <= 1 - inset - safetyPadding
        );
    }

    async updateAutoZoom(region) {
        const now = Date.now();
        if (
            now < this.autoZoomPausedUntil ||
            Number(region?.confidence) < this.LOCAL_OCR_MIN_CONFIDENCE
        ) {
            return;
        }

        const measurement = this.getAutoZoomMeasurement(region);
        if (!measurement || !Number.isFinite(measurement.lineHeightRatio)) return;

        this.autoZoomMissingFrames = 0;
        this.autoZoomSamples.push(measurement);
        if (this.autoZoomSamples.length > this.AUTO_ZOOM_STABLE_OBSERVATIONS) {
            this.autoZoomSamples.shift();
        }
        if (this.autoZoomSamples.length < this.AUTO_ZOOM_STABLE_OBSERVATIONS) {
            return;
        }

        const samples = [...this.autoZoomSamples].sort(
            (first, second) => first.lineHeightRatio - second.lineHeightRatio
        );
        const stableMeasurement = samples[Math.floor(samples.length / 2)];
        const ratio = stableMeasurement.lineHeightRatio;
        if (
            ratio >= this.AUTO_ZOOM_MIN_LINE_RATIO &&
            ratio <= this.AUTO_ZOOM_MAX_LINE_RATIO
        ) {
            this.autoZoomSamples = [];
            return;
        }
        if (now - this.autoZoomLastAdjustedAt < this.AUTO_ZOOM_COOLDOWN_MS) return;

        const direction = ratio < this.AUTO_ZOOM_MIN_LINE_RATIO ? 1 : -1;
        const nextZoom = Math.max(
            this.MIN_ZOOM,
            Math.min(this.MAX_ZOOM, this.zoomLevel + direction * this.ZOOM_STEP)
        );
        if (
            nextZoom === this.zoomLevel ||
            !this.canKeepRegionInNextZoom(stableMeasurement, nextZoom)
        ) {
            this.autoZoomSamples = [];
            return;
        }

        this.autoZoomLastAdjustedAt = now;
        this.autoZoomSamples = [];
        await this.setZoomLevel(nextZoom);
    }

    async recordAutoZoomMiss() {
        if (Date.now() < this.autoZoomPausedUntil) return;

        this.autoZoomSamples = [];
        this.autoZoomMissingFrames += 1;
        if (
            this.autoZoomMissingFrames < this.AUTO_ZOOM_RECOVERY_MISSES ||
            this.zoomLevel <= this.MIN_ZOOM
        ) {
            return;
        }

        this.autoZoomMissingFrames = 0;
        this.autoZoomLastAdjustedAt = Date.now();
        await this.setZoomLevel(this.zoomLevel - this.ZOOM_STEP);
    }

    async startCamera(container) {
        this.shownCaptions = new Set();
        this.shownCaptionTimes = new Map();
        this.consecutiveUsageHeartbeatFailures = 0;
        this.resetAutoZoom();
        this.resetVisionGate();
        // Touch devices read captions server-side and need no local engine.
        const ocrReadyPromise =
            this.ocrStrategy === 'vision' ? null : this.initializeOcrEngine();

        try {
            this.facingMode = 'environment';
            this.cameraStream = await this.requestCameraStream();
            this.updateCameraCapabilities();

            // Create video element
            this.videoElement = document.createElement('video');
            this.videoElement.srcObject = this.cameraStream;
            this.videoElement.setAttribute('playsinline', 'true');
            this.videoElement.setAttribute('muted', 'true');
            this.videoElement.muted = true;

            // Create canvas for frame capture
            this.canvasElement = document.createElement('canvas');
            this.canvasElement.style.display = 'none';
            document.body.appendChild(this.canvasElement);
            this.analysisCanvasElement = document.createElement('canvas');
            this.refinementCanvasElement = document.createElement('canvas');
            this.visionGateCanvasElement = document.createElement('canvas');
            this.visionUploadCanvasElement = document.createElement('canvas');

            // Style video
            this.videoElement.style.cssText = `
                width: 100%;
                height: 100%;
                object-fit: contain;
                display: block;
                transform-origin: center center;
                transition: transform 0.2s ease;
                border-radius: 1em;
            `;

            container.appendChild(this.videoElement);
            await this.videoElement.play();
            await this.detectMultipleCameras();

            if (this.ocrStrategy === 'vision') {
                this.createVisionReadZone(container);
            }

            // Request wake lock to keep screen on
            try {
                if ('wakeLock' in navigator) {
                    this.wakeLock = await navigator.wakeLock.request('screen');
                }
            } catch (err) {
                console.log('Wake lock not supported or denied:', err);
            }

            if (ocrReadyPromise) {
                const ocrEngine = await ocrReadyPromise;
                if (!ocrEngine) {
                    // Some desktops cannot run the local engine (old Safari
                    // without WASM SIMD, WASM disabled by policy). Read
                    // captions server-side instead of failing the camera.
                    this.ocrStrategy = 'vision';
                    this.createVisionReadZone(container);
                    this.notifyOcrStatus(
                        'desktop-vision-fallback',
                        'The local caption reader could not start, so captions are read online instead.'
                    );
                }
            }

            // Start capturing
            this.isListening = true;
            this.lastCaptionTime = Date.now();
            this.startUsageHeartbeat();
            this.captionInterval = setInterval(() => {
                this.captureAndReadCaption();
            }, this.CAPTION_INTERVAL_MS);

            // Auto-close check
            this.noCaptionTimeoutCheck = setInterval(() => {
                if (
                    this.lastCaptionTime &&
                    Date.now() - this.lastCaptionTime >= this.NO_CAPTION_TIMEOUT_MS
                ) {
                    this.notifyOcrStatus(
                        'no-captions-timeout',
                        'Camera closed because no captions were detected for a minute. Move closer and try again.'
                    );
                    this.cleanup();
                    if (this.onClose) this.onClose();
                }
            }, 10000);

            if (this.onReady) this.onReady();
            this.captureAndReadCaption();

            return true;
        } catch (error) {
            console.error('Error starting camera:', error);
            this.notifyOcrStatus(
                'camera-start-error',
                this.getCameraStartErrorMessage(error)
            );
            this.cleanup();
            return false;
        }
    }

    getCameraStartErrorMessage(error) {
        if (error?.name === 'NotAllowedError') {
            return 'Camera access was denied. Allow camera access and try again.';
        }
        if (error?.name === 'NotFoundError') {
            return 'No camera was found on this device.';
        }
        if (['NotReadableError', 'AbortError'].includes(error?.name)) {
            return 'The camera could not start. Close other apps using it and try again.';
        }
        if (error?.message?.includes('caption reader')) {
            return 'The local caption reader could not start. Reload the page and check your connection.';
        }
        return 'The camera could not start. Check its permissions and try again.';
    }

    async captureAndReadCaption() {
        if (
            !this.videoElement ||
            !this.canvasElement ||
            !this.isListening ||
            this.isSwitchingCamera ||
            this.captureInFlight
        ) return;

        this.captureInFlight = true;

        try {
            const videoWidth = this.videoElement.videoWidth;
            const videoHeight = this.videoElement.videoHeight;
            if (!videoWidth || !videoHeight) return;

            const ctx = this.canvasElement.getContext('2d');

            if (this.supportsNativeZoom && this.zoomLevel > 1) {
                this.canvasElement.width = videoWidth;
                this.canvasElement.height = videoHeight;
                ctx.drawImage(this.videoElement, 0, 0, videoWidth, videoHeight);
            } else {
                const cropWidth = videoWidth / this.zoomLevel;
                const cropHeight = videoHeight / this.zoomLevel;
                const cropX = (videoWidth - cropWidth) / 2;
                const cropY = (videoHeight - cropHeight) / 2;

                this.canvasElement.width = cropWidth;
                this.canvasElement.height = cropHeight;
                ctx.drawImage(
                    this.videoElement,
                    cropX, cropY, cropWidth, cropHeight,
                    0, 0, cropWidth, cropHeight
                );
            }

            if (this.ocrStrategy === 'vision') {
                await this.captureVisionCaption();
                return;
            }

            const region = await this.detectCaptionRegion();
            if (!this.isListening) return;
            this.consecutiveOcrRuntimeErrors = 0;
            if (!region) {
                this.pendingOcrCaption = null;
                await this.recordAutoZoomMiss();
                this.recordOcrFailure('missing');
                return;
            }
            if (!this.isLikelyCaptionCandidate(region)) {
                this.pendingOcrCaption = null;
                await this.recordAutoZoomMiss();
                this.recordOcrFailure('missing');
                return;
            }
            await this.updateAutoZoom(region);
            void this.processOcrCandidate(region);
        } catch (error) {
            // Check for usage limit exceeded (429) — plan limit or daily IP cap
            if (
                error.response?.status === 429 &&
                (error.response?.data?.limitExceeded ||
                    error.response?.data?.reason)
            ) {
                console.log('Usage limit exceeded');
                if (this.onLimitExceeded) {
                    this.onLimitExceeded(error.response.data);
                }
                // Stop the caption interval
                this.cleanup();
                return;
            }
            console.error('Error reading caption:', error);
            this.consecutiveOcrRuntimeErrors += 1;
            if (this.consecutiveOcrRuntimeErrors >= 3) {
                this.notifyOcrStatus(
                    'caption-reader-error',
                    'The caption reader hit a problem. If captions do not return, close and reopen the camera.'
                );
            }
        } finally {
            this.captureInFlight = false;
        }
    }

    resetVisionGate() {
        this.visionGeneration += 1;
        this.previousVisionSignature = null;
        this.lastUploadedVisionSignature = null;
        this.lastUploadedVisionAt = 0;
        this.lastUploadedVisionWasCaption = false;
        this.lastVisionUploadAt = 0;
        this.visionUnstableFrames = 0;
        this.visionUploadInFlight = false;
        this.visionUploadTask = null;
    }

    // The on-screen footprint of the captured frame is always the zoom-1
    // object-fit:contain rect: digital zoom enlarges the picture by z while
    // the capture crops 1/z (they cancel), and native zoom captures the full
    // zoomed frame at scale 1. So the read zone never moves with zoom.
    getVisionReadZoneRect(
        containerWidth,
        containerHeight,
        videoWidth,
        videoHeight
    ) {
        if (!containerWidth || !containerHeight || !videoWidth || !videoHeight) {
            return null;
        }

        const scale = Math.min(
            containerWidth / videoWidth,
            containerHeight / videoHeight
        );
        const contentWidth = videoWidth * scale;
        const contentHeight = videoHeight * scale;
        const height = contentHeight * this.VISION_GATE_STRIP_RATIO;

        return {
            left: (containerWidth - contentWidth) / 2,
            top:
                (containerHeight - contentHeight) / 2 +
                contentHeight -
                height,
            width: contentWidth,
            height,
        };
    }

    // Dim the part of the camera preview that captions are never read from,
    // so a user filming captions positioned near the top can see why nothing
    // is being read and reframe.
    createVisionReadZone(container) {
        this.visionReadZoneContainer = container;

        const zone = document.createElement('div');
        zone.style.cssText = `
            position: absolute;
            display: none;
            pointer-events: none;
            z-index: 5;
            box-shadow: 0 0 0 200vmax rgba(0, 0, 0, 0.82);
            border-top: 1px solid rgba(255, 255, 255, 0.35);
        `;

        const hint = document.createElement('div');
        hint.textContent = 'Captions are read inside the bright area';
        hint.style.cssText = `
            position: absolute;
            display: none;
            left: 0;
            right: 0;
            transform: translateY(-50%);
            text-align: center;
            color: rgba(255, 255, 255, 0.75);
            font-size: 13px;
            pointer-events: none;
            z-index: 6;
        `;

        container.appendChild(zone);
        container.appendChild(hint);
        this.visionReadZoneElement = zone;
        this.visionReadZoneHintElement = hint;

        this.boundUpdateVisionReadZone = () => this.updateVisionReadZone();
        window.addEventListener('resize', this.boundUpdateVisionReadZone);
        // The video's own resize event covers camera switches and device
        // rotation changing the stream's dimensions.
        this.videoElement?.addEventListener(
            'resize',
            this.boundUpdateVisionReadZone
        );
        this.updateVisionReadZone();
    }

    updateVisionReadZone() {
        const container = this.visionReadZoneContainer;
        const zone = this.visionReadZoneElement;
        const hint = this.visionReadZoneHintElement;
        if (!container || !zone) return;

        const rect = this.getVisionReadZoneRect(
            container.clientWidth,
            container.clientHeight,
            this.videoElement?.videoWidth,
            this.videoElement?.videoHeight
        );
        if (!rect) {
            zone.style.display = 'none';
            if (hint) hint.style.display = 'none';
            return;
        }

        zone.style.display = 'block';
        zone.style.left = `${Math.round(rect.left)}px`;
        zone.style.top = `${Math.round(rect.top)}px`;
        zone.style.width = `${Math.round(rect.width)}px`;
        zone.style.height = `${Math.round(rect.height)}px`;

        if (hint) {
            const showHint = rect.top >= 40;
            hint.style.display = showHint ? 'block' : 'none';
            hint.style.top = `${Math.round(rect.top / 2)}px`;
        }
    }

    removeVisionReadZone() {
        if (this.boundUpdateVisionReadZone) {
            window.removeEventListener(
                'resize',
                this.boundUpdateVisionReadZone
            );
            this.videoElement?.removeEventListener(
                'resize',
                this.boundUpdateVisionReadZone
            );
            this.boundUpdateVisionReadZone = null;
        }
        this.visionReadZoneElement?.remove();
        this.visionReadZoneHintElement?.remove();
        this.visionReadZoneElement = null;
        this.visionReadZoneHintElement = null;
        this.visionReadZoneContainer = null;
    }

    // Binarize a small thumbnail of the caption strip (bright subtitle
    // pixels against everything else). Comparing these masks between frames
    // is how the vision path knows a subtitle appeared, changed, or is still
    // the one already read — without running any local OCR.
    computeVisionGateSignature() {
        const gate = this.visionGateCanvasElement;
        const sourceWidth = this.canvasElement?.width;
        const sourceHeight = this.canvasElement?.height;
        if (!gate || !sourceWidth || !sourceHeight) return null;

        gate.width = this.VISION_GATE_WIDTH;
        gate.height = this.VISION_GATE_HEIGHT;
        const context = gate.getContext('2d', { willReadFrequently: true });
        if (!context?.getImageData) return null;

        const stripHeight = Math.max(
            1,
            Math.round(sourceHeight * this.VISION_GATE_STRIP_RATIO)
        );
        context.drawImage(
            this.canvasElement,
            0,
            sourceHeight - stripHeight,
            sourceWidth,
            stripHeight,
            0,
            0,
            gate.width,
            gate.height
        );

        const pixels = context.getImageData(0, 0, gate.width, gate.height).data;
        const mask = new Uint8Array(gate.width * gate.height);
        let brightCount = 0;
        for (let index = 0; index < mask.length; index += 1) {
            const offset = index * 4;
            const brightness =
                pixels[offset] * 0.299 +
                pixels[offset + 1] * 0.587 +
                pixels[offset + 2] * 0.114;
            if (brightness >= this.VISION_GATE_BRIGHTNESS_THRESHOLD) {
                mask[index] = 1;
                brightCount += 1;
            }
        }

        return { mask, brightRatio: brightCount / mask.length };
    }

    getVisionSignatureDiff(first, second) {
        if (!first?.mask || !second?.mask) return 1;
        if (first.mask.length !== second.mask.length) return 1;

        let differing = 0;
        for (let index = 0; index < first.mask.length; index += 1) {
            if (first.mask[index] !== second.mask[index]) differing += 1;
        }
        return differing / first.mask.length;
    }

    encodeVisionFrame() {
        const canvas = this.visionUploadCanvasElement;
        const sourceWidth = this.canvasElement?.width;
        const sourceHeight = this.canvasElement?.height;
        if (!canvas || !sourceWidth || !sourceHeight) {
            return Promise.resolve(null);
        }

        // Upload exactly the strip the gate watches. Anything above it never
        // triggers a read anyway, and cropping keeps the surroundings of the
        // screen out of the request.
        const stripHeight = Math.max(
            1,
            Math.round(sourceHeight * this.VISION_GATE_STRIP_RATIO)
        );
        const scale = Math.min(1, this.VISION_UPLOAD_MAX_WIDTH / sourceWidth);
        canvas.width = Math.max(1, Math.round(sourceWidth * scale));
        canvas.height = Math.max(1, Math.round(stripHeight * scale));
        const context = canvas.getContext('2d');
        if (!context) return Promise.resolve(null);
        context.imageSmoothingEnabled = true;
        context.imageSmoothingQuality = 'high';
        context.drawImage(
            this.canvasElement,
            0,
            sourceHeight - stripHeight,
            sourceWidth,
            stripHeight,
            0,
            0,
            canvas.width,
            canvas.height
        );

        return new Promise(resolve => {
            canvas.toBlob(
                blob => resolve(blob),
                'image/jpeg',
                this.VISION_JPEG_QUALITY
            );
        });
    }

    matchesLastUploadedVisionFrame(signature) {
        if (!this.lastUploadedVisionSignature) return false;
        if (
            this.getVisionSignatureDiff(
                signature,
                this.lastUploadedVisionSignature
            ) >= this.VISION_GATE_CHANGED_DIFF_RATIO
        ) {
            return false;
        }

        // A frame that produced a caption stays suppressed while it is on
        // screen, but a non-caption reading may have been a model false
        // negative; let the same frame become eligible again after a pause.
        return (
            this.lastUploadedVisionWasCaption ||
            Date.now() - this.lastUploadedVisionAt <
                this.VISION_NEGATIVE_RETRY_MS
        );
    }

    // Runs the cheap gate math only. The network round-trip happens in
    // uploadVisionFrame without blocking the capture interval, so frames keep
    // being sampled while a read is in flight.
    async captureVisionCaption() {
        const signature = this.computeVisionGateSignature();
        if (!signature) return;

        if (signature.brightRatio < this.VISION_GATE_MIN_BRIGHT_RATIO) {
            // Nothing subtitle-like on screen; normal between captions.
            this.previousVisionSignature = signature;
            this.visionUnstableFrames = 0;
            return;
        }

        if (this.matchesLastUploadedVisionFrame(signature)) {
            this.previousVisionSignature = signature;
            this.visionUnstableFrames = 0;
            return;
        }

        const previousSignature = this.previousVisionSignature;
        this.previousVisionSignature = signature;
        const isStable =
            previousSignature &&
            this.getVisionSignatureDiff(signature, previousSignature) <=
                this.VISION_GATE_STABLE_DIFF_RATIO;
        if (!isStable) {
            this.visionUnstableFrames += 1;
            // A busy bright background can keep the mask jittering forever;
            // never let that starve caption reading entirely.
            if (this.visionUnstableFrames < this.VISION_GATE_MAX_UNSTABLE_FRAMES) {
                return;
            }
        }

        if (this.visionUploadInFlight) return;
        if (
            Date.now() - this.lastVisionUploadAt <
            this.VISION_MIN_UPLOAD_INTERVAL_MS
        ) {
            return;
        }

        this.visionUnstableFrames = 0;
        this.lastVisionUploadAt = Date.now();
        this.visionUploadInFlight = true;
        const generation = this.visionGeneration;
        const task = this.uploadVisionFrame(signature, generation);
        this.visionUploadTask = task;
        task.finally(() => {
            if (generation === this.visionGeneration) {
                this.visionUploadInFlight = false;
            }
            if (this.visionUploadTask === task) {
                this.visionUploadTask = null;
            }
        });
    }

    async uploadVisionFrame(signature, generation) {
        const frameBlob = await this.encodeVisionFrame();
        if (
            !frameBlob ||
            !this.isListening ||
            generation !== this.visionGeneration
        ) {
            return;
        }

        let result;
        try {
            result = await apiReadCaptionImage(frameBlob);
        } catch (error) {
            if (generation !== this.visionGeneration) return;
            if (
                error?.response?.status === 429 &&
                (error.response?.data?.limitExceeded ||
                    error.response?.data?.reason)
            ) {
                this.handleUsageLimit(error.response.data);
                return;
            }
            this.notifyOcrStatus(
                'caption-reading-unavailable',
                'Captions could not be read right now. Check your connection.'
            );
            return;
        }
        if (!this.isListening || generation !== this.visionGeneration) return;

        const text = this.cleanOcrText(result.text);
        const isCaption =
            result.isCaption && this.normalizeCaption(text).length >= 2;
        this.lastUploadedVisionSignature = signature;
        this.lastUploadedVisionAt = Date.now();
        this.lastUploadedVisionWasCaption = isCaption;
        this.consecutiveOcrRuntimeErrors = 0;

        if (!isCaption) return;
        if (result.sourceLanguage) {
            this.sourceLanguage = result.sourceLanguage;
        }
        if (this.isDuplicateCaption(text)) return;

        this.rememberShownCaption(text);
        this.addCaptionMessage(
            text,
            result.sourceLanguage || this.sourceLanguage
        );
    }

    startUsageHeartbeat() {
        if (this.usageHeartbeatInterval) {
            clearInterval(this.usageHeartbeatInterval);
        }
        this.usageHeartbeatGeneration += 1;
        this.usageSessionId = createUsageSessionId();
        trackCameraStarted(this.usageSessionId);
        this.usageHeartbeatSequence = 0;
        this.pendingUsageHeartbeat = null;
        this.consecutiveUsageHeartbeatFailures = 0;
        this.lastUsageHeartbeatAt = Date.now();
        this.usageHeartbeatInterval = setInterval(() => {
            this.sendUsageHeartbeat();
        }, this.USAGE_HEARTBEAT_MS);
    }

    stopUsageHeartbeat() {
        this.usageHeartbeatGeneration += 1;
        if (this.usageHeartbeatInterval) {
            clearInterval(this.usageHeartbeatInterval);
            this.usageHeartbeatInterval = null;
        }

        if (this.usageHeartbeatInFlight) {
            this.lastUsageHeartbeatAt = null;
            return;
        }

        if (this.pendingUsageHeartbeat) {
            const pendingHeartbeat = this.pendingUsageHeartbeat;
            this.pendingUsageHeartbeat = null;
            this.lastUsageHeartbeatAt = null;
            api.post(
                '/api/captions/billing/usage-heartbeat',
                pendingHeartbeat
            ).catch(() => {});
            return;
        }

        const now = Date.now();
        const rawElapsedSeconds = this.lastUsageHeartbeatAt
            ? (now - this.lastUsageHeartbeatAt) / 1000
            : 0;
        if (rawElapsedSeconds > (this.USAGE_HEARTBEAT_MS / 1000) * 3) {
            // As with the periodic heartbeat, do not bill time spent while the
            // browser timer was suspended in the background.
            this.lastUsageHeartbeatAt = null;
            return;
        }
        const elapsedSeconds = Math.min(30, Math.floor(rawElapsedSeconds));
        this.lastUsageHeartbeatAt = null;

        if (
            elapsedSeconds >= 1 &&
            !this.usageHeartbeatInFlight &&
            this.usageSessionId
        ) {
            this.usageHeartbeatSequence += 1;
            api.post('/api/captions/billing/usage-heartbeat', {
                elapsedSeconds,
                sessionId: this.usageSessionId,
                sequence: this.usageHeartbeatSequence,
            }).catch(() => {});
        }
    }

    async sendUsageHeartbeat() {
        if (
            this.usageHeartbeatInFlight ||
            !this.isListening ||
            !this.lastUsageHeartbeatAt
        ) {
            return;
        }

        const heartbeatGeneration = this.usageHeartbeatGeneration;
        let heartbeat = this.pendingUsageHeartbeat;

        if (!heartbeat) {
            const now = Date.now();
            const rawElapsedSeconds = (now - this.lastUsageHeartbeatAt) / 1000;
            if (rawElapsedSeconds < 1) return;
            if (rawElapsedSeconds > (this.USAGE_HEARTBEAT_MS / 1000) * 3) {
                // Timer suspension means the captions were not actively viewed.
                this.lastUsageHeartbeatAt = now;
                return;
            }

            const elapsedSeconds = Math.min(30, Math.floor(rawElapsedSeconds));
            const unreportedSeconds = rawElapsedSeconds - elapsedSeconds;
            this.lastUsageHeartbeatAt = now - unreportedSeconds * 1000;
            if (elapsedSeconds < 1 || !this.usageSessionId) return;

            this.usageHeartbeatSequence += 1;
            heartbeat = {
                elapsedSeconds,
                sessionId: this.usageSessionId,
                sequence: this.usageHeartbeatSequence,
            };
            this.pendingUsageHeartbeat = heartbeat;
        }

        this.usageHeartbeatInFlight = true;

        try {
            const response = await api.post(
                '/api/captions/billing/usage-heartbeat',
                heartbeat
            );

            if (heartbeatGeneration !== this.usageHeartbeatGeneration) return;
            this.pendingUsageHeartbeat = null;
            this.consecutiveUsageHeartbeatFailures = 0;
            if (response.data?.can_stream === false) {
                this.handleUsageLimit(response.data);
            }
        } catch (error) {
            if (heartbeatGeneration !== this.usageHeartbeatGeneration) {
                return;
            }
            if (
                error.response?.status === 429 &&
                (error.response?.data?.limitExceeded ||
                    error.response?.data?.reason)
            ) {
                this.pendingUsageHeartbeat = null;
                this.handleUsageLimit(error.response.data);
            } else if ([401, 403].includes(error.response?.status)) {
                this.pendingUsageHeartbeat = null;
                this.stopForUsageHeartbeatError(
                    'Your sign-in expired, so captions were stopped. Sign in again to continue.'
                );
            } else {
                // Retry this exact payload and idempotency key. If the server
                // committed it before the response was lost, it will return
                // the current entitlement without charging it twice.
                this.consecutiveUsageHeartbeatFailures += 1;
                console.warn('Could not record caption viewing time:', error);
                if (
                    this.consecutiveUsageHeartbeatFailures >=
                    this.MAX_USAGE_HEARTBEAT_FAILURES
                ) {
                    this.stopForUsageHeartbeatError(
                        'Captions were stopped because usage could not be recorded. Check your connection and try again.'
                    );
                }
            }
        } finally {
            this.usageHeartbeatInFlight = false;
        }
    }

    handleUsageLimit(payload) {
        if (this.onLimitExceeded) {
            this.onLimitExceeded(payload);
        }
        this.cleanup();
    }

    stopForUsageHeartbeatError(message) {
        if (this.onOcrStatus) {
            this.onOcrStatus(message);
        }
        this.cleanup();
        if (this.onClose) {
            this.onClose();
        }
    }

    cleanOcrText(text) {
        if (typeof text !== 'string') return '';
        const lines = text
            .split(/\r?\n/)
            .map(line => {
                const normalized = line.replace(/\s+/g, ' ').trim();
                const hasSpeakerDash = /^[-‐‑‒–—−]\s*/.test(normalized);
                return {
                    text: normalized.replace(/^[-‐‑‒–—−]\s*/, '').trim(),
                    hasSpeakerDash,
                };
            })
            .filter(line => line.text);

        return lines.reduce((caption, line, index) => {
            if (index === 0) return line.text;
            return `${caption}${line.hasSpeakerDash ? '\n' : ' '}${line.text}`;
        }, '');
    }

    areCaptionKeysSimilar(first, second) {
        if (!first || !second) return false;
        if (first === second) return true;
        if (
            Math.min(first.length, second.length) >= 4 &&
            (first.includes(second) || second.includes(first))
        ) {
            return true;
        }

        const firstWords = first.split(' ');
        const secondWords = second.split(' ');
        const matchingWords = secondWords.filter(word => firstWords.includes(word));
        if (
            matchingWords.length /
                Math.max(firstWords.length, secondWords.length) >
            0.8
        ) {
            return true;
        }

        return this.getEditSimilarity(first, second) >= 0.82;
    }

    getEditSimilarity(first, second) {
        if (!first || !second) return 0;
        const previous = Array.from({ length: second.length + 1 }, (_, index) => index);

        for (let firstIndex = 1; firstIndex <= first.length; firstIndex += 1) {
            const current = [firstIndex];
            for (let secondIndex = 1; secondIndex <= second.length; secondIndex += 1) {
                const substitutionCost =
                    first[firstIndex - 1] === second[secondIndex - 1] ? 0 : 1;
                current[secondIndex] = Math.min(
                    current[secondIndex - 1] + 1,
                    previous[secondIndex] + 1,
                    previous[secondIndex - 1] + substitutionCost
                );
            }
            previous.splice(0, previous.length, ...current);
        }

        return 1 - previous[second.length] / Math.max(first.length, second.length);
    }

    notifyOcrStatus(key, message) {
        if (!this.onOcrStatus) return;

        const now = Date.now();
        const previousNotificationAt = this.ocrNotificationTimes.get(key) || 0;
        if (now - previousNotificationAt < this.OCR_NOTIFICATION_COOLDOWN_MS) {
            return;
        }
        this.ocrNotificationTimes.set(key, now);
        this.onOcrStatus(message);
    }

    recordOcrFailure(kind = 'unreadable') {
        // A frame with no detected text is normal between subtitle cards. It
        // must not produce an "unreadable" warning after only a few seconds.
        if (kind === 'missing') {
            this.consecutiveOcrFailures = 0;
            this.consecutiveUnreadableOcrFailures = 0;
            return;
        }

        this.consecutiveOcrFailures += 1;
        this.consecutiveUnreadableOcrFailures =
            kind === 'unreadable'
                ? this.consecutiveUnreadableOcrFailures + 1
                : 0;

        if (
            kind === 'unreadable' &&
            this.consecutiveUnreadableOcrFailures >=
                this.OCR_UNSUPPORTED_FAILURES
        ) {
            this.notifyOcrStatus(
                'unsupported-language',
                "These captions may use a script that local OCR doesn't support yet."
            );
            return;
        }

        if (this.consecutiveOcrFailures >= this.OCR_GUIDANCE_FAILURES) {
            this.notifyOcrStatus(
                'caption-quality',
                'Captions are difficult to read. Move closer or zoom in, and hold the camera steady.'
            );
        }
    }

    async processOcrCandidate(region) {
        const text = this.cleanOcrText(region.text);
        const key = this.normalizeCaption(text);
        const confidence = Number(region.confidence);
        const isUsable =
            confidence >= this.LOCAL_OCR_MIN_CONFIDENCE && key.length >= 2;

        if (!isUsable) {
            this.pendingOcrCaption = null;
            this.recordOcrFailure('unreadable');
            return;
        }
        if (!this.isLikelyCaptionCandidate(region, text)) {
            this.pendingOcrCaption = null;
            this.recordOcrFailure('missing');
            return;
        }

        this.consecutiveOcrFailures = 0;
        this.consecutiveUnreadableOcrFailures = 0;

        if (this.isDuplicateCaption(text)) {
            this.pendingOcrCaption = null;
            return;
        }

        // Very confident captions are displayed immediately. Requiring a
        // second full-frame OCR pass caused short TV subtitles to disappear
        // before they could ever be accepted.
        if (confidence >= this.LOCAL_OCR_IMMEDIATE_CONFIDENCE) {
            this.pendingOcrCaption = null;
            this.rememberShownCaption(text);
            await this.proofreadAndAddCaption(text, region);
            return;
        }

        if (
            this.pendingOcrCaption &&
            this.areCaptionKeysSimilar(this.pendingOcrCaption.key, key)
        ) {
            this.pendingOcrCaption.observations += 1;
            this.pendingOcrCaption.text = text;
            this.pendingOcrCaption.key = key;
            this.pendingOcrCaption.region = region;

            if (
                this.pendingOcrCaption.observations >=
                this.LOCAL_OCR_STABLE_OBSERVATIONS
            ) {
                const stableCaption = this.pendingOcrCaption;
                this.pendingOcrCaption = null;

                if (!this.isDuplicateCaption(stableCaption.text)) {
                    this.rememberShownCaption(stableCaption.text);
                    await this.proofreadAndAddCaption(
                        stableCaption.text,
                        stableCaption.region
                    );
                }
            }
            return;
        }

        this.pendingOcrCaption = {
            key,
            text,
            region,
            observations: 1,
        };
    }

    async proofreadAndAddCaption(text, region) {
        const generation = this.captionProofreadGeneration;
        // Start each small network request immediately, but publish through a
        // queue so variable model latency cannot reorder consecutive captions.
        const proofreadPromise = apiProofreadCaption(
            text,
            this.getCaptionLayout(region),
            (error) => {
                if (generation !== this.captionProofreadGeneration) return;
                if (
                    error?.response?.status === 429 &&
                    error?.response?.data?.reason === 'plan_limit'
                ) {
                    this.handleUsageLimit(error.response.data);
                    return;
                }
                this.notifyOcrStatus(
                    'proofreading-unavailable',
                    'Caption proofreading is unavailable. Showing uncorrected captions for now.'
                );
            }
        );
        const publish = async () => {
            const proofreadResult = await proofreadPromise;
            if (!proofreadResult.isCaption) return;
            const proofreadText = this.cleanOcrText(
                proofreadResult.text
            ) || text;
            if (generation !== this.captionProofreadGeneration) return;

            if (proofreadResult.sourceLanguage) {
                this.sourceLanguage = proofreadResult.sourceLanguage;
            }

            this.rememberShownCaption(proofreadText);
            this.addCaptionMessage(
                proofreadText,
                proofreadResult.sourceLanguage || this.sourceLanguage
            );
        };
        const task = this.captionProofreadQueue.then(publish, publish);
        this.captionProofreadQueue = task.catch(() => {});
        return task;
    }

    async initializeOcrEngine() {
        if (this.ocrEngine) return this.ocrEngine;
        if (this.ocrEnginePromise) return this.ocrEnginePromise;
        if (Date.now() < this.ocrRetryAfter) return null;

        const generation = this.ocrEngineGeneration;
        const enginePromise = createSharedOcrEngine()
            .then(async engine => {
                if (generation !== this.ocrEngineGeneration) {
                    return null;
                }

                this.ocrEngine = engine;
                return engine;
            })
            .catch(error => {
                this.ocrRetryAfter = Date.now() + 30000;
                console.warn('Local PaddleOCR could not be initialized:', error);
                return null;
            });

        this.ocrEnginePromise = enginePromise;
        enginePromise.finally(() => {
            if (this.ocrEnginePromise === enginePromise) {
                this.ocrEnginePromise = null;
            }
        });

        return enginePromise;
    }

    terminateOcrEngine() {
        this.ocrEngineGeneration += 1;
        this.ocrEngine = null;
        this.ocrEnginePromise = null;
        this.analysisCanvasElement = null;
        this.refinementCanvasElement = null;
    }

    async refineCaptionRegion(engine, region) {
        if (
            !region ||
            !this.canvasElement ||
            !this.analysisCanvasElement ||
            !this.refinementCanvasElement
        ) {
            return region;
        }

        const sourceWidth = this.canvasElement.width;
        const sourceHeight = this.canvasElement.height;
        const analysisWidth = this.analysisCanvasElement.width;
        const analysisHeight = this.analysisCanvasElement.height;
        if (!sourceWidth || !sourceHeight || !analysisWidth || !analysisHeight) {
            return region;
        }

        const left = Math.max(
            0,
            Math.floor(region.left / analysisWidth * sourceWidth)
        );
        const top = Math.max(
            0,
            Math.floor(region.top / analysisHeight * sourceHeight)
        );
        const right = Math.min(
            sourceWidth,
            Math.ceil(
                (region.left + region.width) / analysisWidth * sourceWidth
            )
        );
        const bottom = Math.min(
            sourceHeight,
            Math.ceil(
                (region.top + region.height) / analysisHeight * sourceHeight
            )
        );
        const cropWidth = right - left;
        const cropHeight = bottom - top;
        if (cropWidth < 2 || cropHeight < 2) return region;

        const lineCount = Math.max(
            1,
            String(region.text || '').split('\n').filter(Boolean).length
        );
        const desiredScale = Math.max(1, lineCount * 96 / cropHeight);
        const outputScale = Math.max(
            0.25,
            Math.min(
                3,
                desiredScale,
                this.OCR_MAX_DIMENSION / cropWidth,
                this.OCR_MAX_DIMENSION / cropHeight
            )
        );

        this.refinementCanvasElement.width = Math.max(
            1,
            Math.round(cropWidth * outputScale)
        );
        this.refinementCanvasElement.height = Math.max(
            1,
            Math.round(cropHeight * outputScale)
        );
        const context = this.refinementCanvasElement.getContext('2d', {
            willReadFrequently: true,
        });
        if (!context) return region;
        context.imageSmoothingEnabled = true;
        context.imageSmoothingQuality = 'high';
        context.drawImage(
            this.canvasElement,
            left,
            top,
            cropWidth,
            cropHeight,
            0,
            0,
            this.refinementCanvasElement.width,
            this.refinementCanvasElement.height
        );

        const [refinedResult] = await engine.predict(
            this.refinementCanvasElement,
            {
                textDetLimitSideLen: this.OCR_MAX_DIMENSION,
                textDetLimitType: 'max',
                textDetMaxSideLimit: 1600,
                textDetThresh: 0.2,
                textDetBoxThresh: 0.35,
                textDetUnclipRatio: 1.5,
                textRecScoreThresh: 0.45,
            }
        );
        const refined = this.selectCaptionRegionFromResult(
            refinedResult,
            this.refinementCanvasElement.width,
            this.refinementCanvasElement.height
        );
        if (!refined) return region;

        const originalCharacters =
            (String(region.text).match(/[\p{L}\p{N}]/gu) || []).length;
        const refinedCharacters =
            (String(refined.text).match(/[\p{L}\p{N}]/gu) || []).length;
        if (
            refined.confidence < Math.max(40, region.confidence - 20) ||
            refinedCharacters < originalCharacters * 0.6
        ) {
            return region;
        }

        // Keep the full-frame geometry for auto-zoom, but use the more legible
        // crop's recognition result for caption text and confidence.
        return {
            ...region,
            text: refined.text,
            confidence: refined.confidence,
        };
    }

    async detectCaptionRegion() {
        const engine = await this.initializeOcrEngine();
        if (!engine || !this.analysisCanvasElement) return null;

        const sourceWidth = this.canvasElement.width;
        const sourceHeight = this.canvasElement.height;
        const scale = Math.min(
            1,
            this.OCR_MAX_DIMENSION / Math.max(sourceWidth, sourceHeight)
        );
        this.analysisCanvasElement.width = Math.max(1, Math.round(sourceWidth * scale));
        this.analysisCanvasElement.height = Math.max(1, Math.round(sourceHeight * scale));

        const context = this.analysisCanvasElement.getContext('2d', {
            willReadFrequently: true,
        });
        context.drawImage(
            this.canvasElement,
            0,
            0,
            this.analysisCanvasElement.width,
            this.analysisCanvasElement.height
        );
        const [result] = await engine.predict(this.analysisCanvasElement, {
            textDetLimitSideLen: this.OCR_MAX_DIMENSION,
            textDetLimitType: 'max',
            textDetMaxSideLimit: 1600,
            textDetThresh: 0.25,
            textDetBoxThresh: 0.45,
            textDetUnclipRatio: 1.5,
            textRecScoreThresh: 0.45,
        });

        const region = this.selectCaptionRegionFromResult(
            result,
            this.analysisCanvasElement.width,
            this.analysisCanvasElement.height
        );
        if (!region) return null;

        return this.refineCaptionRegion(engine, region);
    }

    selectCaptionRegionFromResult(result, imageWidth, imageHeight) {
        return selectPaddleCaptionRegion(
            result?.items,
            imageWidth,
            imageHeight
        );
    }

    normalizeCaption(text) {
        return text
            .normalize('NFC')
            .toLowerCase()
            .replace(/[^\p{L}\p{N}\s]/gu, '')
            .replace(/\s+/g, ' ')
            .trim();
    }

    isDuplicateCaption(newCaption) {
        this.pruneShownCaptions();
        const normalizedNew = this.normalizeCaption(newCaption);

        if (normalizedNew.length < 2) return true;

        for (const existing of this.shownCaptions) {
            if (existing === normalizedNew) return true;

            // Suppress minor OCR variations, but do not discard a later
            // caption merely because it extends a previously shown fragment.
            const lengthRatio =
                Math.min(existing.length, normalizedNew.length) /
                Math.max(existing.length, normalizedNew.length);
            if (lengthRatio < 0.75) continue;

            if (
                existing.includes(normalizedNew) ||
                normalizedNew.includes(existing)
            ) {
                return true;
            }

            const existingWords = existing.split(' ');
            const newWords = normalizedNew.split(' ');
            const matchingWords = newWords.filter(word =>
                existingWords.includes(word)
            );
            const similarity =
                matchingWords.length /
                Math.max(existingWords.length, newWords.length);
            if (similarity > 0.9) return true;
            if (this.getEditSimilarity(existing, normalizedNew) >= 0.82) {
                return true;
            }
        }

        return false;
    }

    rememberShownCaption(text) {
        const shownAt = Date.now();
        const keys = [text, ...text.split(/\r?\n/)]
            .map(value => this.normalizeCaption(value))
            .filter(key => key.length >= 2);
        keys.forEach(key => {
            this.shownCaptions.add(key);
            this.shownCaptionTimes.set(key, shownAt);
        });
    }

    pruneShownCaptions(now = Date.now()) {
        for (const [key, shownAt] of this.shownCaptionTimes) {
            if (now - shownAt > this.SHOWN_CAPTION_TTL_MS) {
                this.shownCaptionTimes.delete(key);
                this.shownCaptions.delete(key);
            }
        }
    }

    addCaptionMessage(text, sourceLanguage = null) {
        if (!text) return;

        const timestamp = Date.now();
        this.lastCaptionTime = timestamp;

        this.addMessage({
            id: `caption-${timestamp}`,
            content: text,
            sender: 'caption',
            timestamp,
            isCaption: true,
            sourceLanguage,
            analyticsSessionId: this.usageSessionId,
        });

        // Broadcast via WebSocket
        const wasBroadcast = websocketService.sendCaption(
            text,
            timestamp,
            sourceLanguage,
            this.usageSessionId
        );
        if (!wasBroadcast) {
            this.notifyOcrStatus(
                'caption-handoff-disconnected',
                'Live hand-off is reconnecting. Captions are still shown on this device.'
            );
        }
    }
}
